/**
 * The desktop-notification opt-in for every user on this browser lives under one
 * key holding a { [userId]: boolean } map. It cannot be keyed per user, because the
 * user id itself comes from `currentUser`, which logout destroys.
 */
export const DESKTOP_NOTIFICATION_PREFERENCE_KEY = 'wolsal.ui.notifications.desktop';

/**
 * Standalone UI preferences (see `UiPreferencesService`) share one key holding a
 * flat { [preference]: boolean } map. Declared here so the key and the fact that
 * it survives a logout cannot drift apart.
 */
export const UI_PREFERENCES_STORAGE_KEY = 'wolsal.ui.preferences';

/**
 * First-time-experience state (which tips a user has seen, and whether tips are
 * on) for every user on this browser, under one key holding a
 * { [userId]: state } map. It cannot be keyed per user: this list matches exact
 * keys, so a `…ftue.v1.<userId>` key would be wiped on every logout and every
 * "you will only see this once" promise would be broken. See
 * `core/ftue/local-storage-ftue-state.store.ts`.
 */
export const FTUE_STORAGE_KEY = 'wolsal.ui.ftue.v1';

/**
 * Where the recipient-mailbox tag is shown in Smart Inbox (see
 * `MailboxTagPreferenceService`). A display preference for this browser, so it has no
 * business being destroyed by a logout — least of all by an involuntary 401.
 */
export const MAILBOX_TAG_DISPLAY_STORAGE_KEY = 'smartInbox.mailboxTagDisplay';

/**
 * localStorage keys that outlive a logout: device or browser preferences and
 * cache-bust markers. Session data (currentUser, feature filters) is not listed and
 * is therefore cleared.
 */
export const PRESERVED_STORAGE_KEYS: readonly string[] = [
  'lang',
  'app_version',
  'wolsal.ui.tables',
  'wolsal.ui.tables.migrations',
  DESKTOP_NOTIFICATION_PREFERENCE_KEY,
  UI_PREFERENCES_STORAGE_KEY,
  FTUE_STORAGE_KEY,
  MAILBOX_TAG_DISPLAY_STORAGE_KEY,
];

/**
 * Wipe the session from localStorage, keeping only PRESERVED_STORAGE_KEYS. The single
 * clearing path for every logout route (401 interceptor and both Logout buttons).
 */
export function clearStorageExceptPreserved(): void {
  const preserved = PRESERVED_STORAGE_KEYS
    .map((key) => [key, localStorage.getItem(key)] as const)
    .filter((entry): entry is readonly [string, string] => entry[1] !== null);

  localStorage.clear();

  preserved.forEach(([key, value]) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore quota / disabled storage errors — restoring a preference is best-effort
    }
  });
}
